import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useDrag } from 'react-dnd';
import CreateTagModal from '../modal/CreateTagModal.jsx';
import ConfirmationModal from '../modal/ConfirmationModal.jsx';
import { resolveMediaUrl } from '../../../_helpers/mediaUrl.js';
import { serverMessage } from '../../../_helpers/errorMessage.js';
import { FaLock, FaGlobe, FaEdit, FaTrash, FaArrowsAlt } from 'react-icons/fa';
import { useFetchWrapper } from '../../../_helpers/fetchWrapper.js';
import { useReload } from '../../../contexts/ReloadContext.jsx';
import styles from '../../../styles/partial/PerformerNavEditTags.module.css';
import config from '../../../config.js';
import { useT } from '../../../contexts/LocaleContext.jsx';

export const ItemTypes = {
  TAG: 'tag',
};

const DraggableTag = ({ tag, handleEdit, handleDelete }) => {
  const t = useT();
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.TAG,
    item: { tag },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  const rawTagBgColor = tag.BackgroundColor || tag.background_color;
  const rawTagTextColor = tag.TextColor || tag.text_color;
  const usesLegacyDefaultColors = ['#fff', '#ffffff', 'white', 'rgb(255, 255, 255)'].includes(
    String(rawTagBgColor).trim().toLowerCase()
  );
  const hasCustomColors = rawTagBgColor && !usesLegacyDefaultColors;
  const tagImageUrl = tag.ImageURL || tag.image_url;
  const tagWord = tag.Word || tag.word;
  const tagId = tag.ID || tag.id;

  return (
    <div
      ref={drag}
      className={styles.tagItem}
      data-testid={`tag-draggable-${tagId}`}
      style={{ opacity: isDragging ? 0.4 : 1 }}
    >
      <FaArrowsAlt className={styles.tagDragHandle} />
      <span
        className={hasCustomColors ? styles.tagPillCustom : styles.tagPill}
        style={hasCustomColors ? { backgroundColor: rawTagBgColor, color: rawTagTextColor } : undefined}
      >
        {tagImageUrl && (
          <img
            src={resolveMediaUrl(tagImageUrl)}
            alt=""
            style={{ width: 14, height: 14, borderRadius: 2, flexShrink: 0 }}
          />
        )}
        {tagWord}
      </span>
      <div className={styles.tagSpacer} />
      <div className={styles.tagActions}>
        <button
          type="button"
          className={styles.tagActionBtn}
          onClick={() => handleEdit(tag)}
          aria-label={t('editTags.nav.editTag', { tag: tagWord })}
        >
          <FaEdit />
        </button>
        <button
          type="button"
          className={styles.tagActionBtn}
          onClick={() => handleDelete(tag)}
          aria-label={t('editTags.nav.deleteTag', { tag: tagWord })}
        >
          <FaTrash />
        </button>
      </div>
    </div>
  );
};

const PerformerNavEditTags = () => {
  const { bandid } = useParams();
  const t = useT();
  const fetchWrapperRef = useRef(useFetchWrapper());
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  // The tag awaiting a Delete confirmation (ENG-509), and why the last
  // delete failed.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { reload } = useReload();

  const openModal = (tag = null) => {
    setSelectedTag(tag);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedTag(null);
  };

  const closeModalWithReload = async () => {
    setModalIsOpen(false);
    setSelectedTag(null);
    await fetchTags();
    reload();
  };

  const fetchTags = useCallback(async () => {
    try {
      const response = await fetchWrapperRef.current.get(`${config.apiBaseUrl}/bands/${bandid}/tags`);

      if (response && response.success && Array.isArray(response.tags)) {
        setTags(response.tags);
      } else if (Array.isArray(response)) {
        setTags(response);
      } else {
        console.warn('Unexpected tags response format:', response);
        setTags([]);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
      setTags([]);
    }
  }, [bandid]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const handleEdit = (tag) => {
    openModal(tag);
  };

  // The trash icon asks first (ENG-509); it used to delete on the first click.
  const handleDelete = (tag) => {
    setDeleteError(null);
    setPendingDelete(tag);
  };

  const confirmDelete = async () => {
    if (!pendingDelete || isDeleting) return;
    const tagId = pendingDelete.ID || pendingDelete.id;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await fetchWrapperRef.current.delete(`${config.apiBaseUrl}/bands/${bandid}/tags/${tagId}`);
      setPendingDelete(null);
      await fetchTags();
      reload();
    } catch (error) {
      console.error('Error deleting tag:', error);
      setDeleteError(serverMessage(error) || t('editTags.nav.deleteFailed'));
    } finally {
      setIsDeleting(false);
    }
  };

  const privateTags = tags.filter(tag => !(tag.IsPublic ?? tag.is_public));
  const publicTags = tags.filter(tag => !!(tag.IsPublic ?? tag.is_public));

  return (
    <div className={styles.nav}>
      <h3 className={styles.sectionTitle}>{t('editTags.nav.title')}</h3>

      <div className={styles.sectionLabel}>
        <FaLock className={styles.sectionLabelIcon} aria-hidden="true" />
        {t('editTags.nav.privateTags')}
      </div>

      {privateTags.length === 0 ? (
        <div className={styles.privateEmpty}>
          <span className={styles.privateEmptyText} style={{ whiteSpace: 'pre-line' }}>
            {t('editTags.nav.privateEmpty')}
          </span>
        </div>
      ) : (
        <div className={styles.tagsList}>
          {privateTags.map(tag => (
            <DraggableTag
              key={tag.ID || tag.id}
              tag={tag}
              handleEdit={handleEdit}
              handleDelete={handleDelete}
            />
          ))}
        </div>
      )}

      <div className={styles.sectionLabel}>
        <FaGlobe className={styles.sectionLabelIcon} aria-hidden="true" />
        {t('editTags.nav.publicTags')}
      </div>

      {publicTags.length > 0 ? (
        <div className={styles.tagsList}>
          {publicTags.map(tag => (
            <DraggableTag
              key={tag.ID || tag.id}
              tag={tag}
              handleEdit={handleEdit}
              handleDelete={handleDelete}
            />
          ))}
        </div>
      ) : (
        <div className={styles.publicEmpty}>
          <span className={styles.publicEmptyText} style={{ whiteSpace: 'pre-line' }}>
            {t('editTags.nav.publicEmpty')}
          </span>
        </div>
      )}

      <button
        type="button"
        onClick={() => openModal()}
        className={styles.newTagBtn}
      >
        {t('editTags.nav.newTag')}
      </button>

      <CreateTagModal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        onSave={closeModalWithReload}
        tag={selectedTag}
      />

      <ConfirmationModal
        isOpen={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        confirmTestId="tag-delete-confirm"
        title={t('editTags.nav.deleteModalTitle')}
        message={pendingDelete ? t('editTags.nav.deleteModalMessage', { tag: pendingDelete.Word || pendingDelete.word }) : ''}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        isDangerous={true}
        error={deleteError}
        isBusy={isDeleting}
      />
    </div>
  );
};

export default PerformerNavEditTags;
